import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { RootState } from 'redux-setup/store';
import { listApps } from 'services/appService';
import { toast } from 'react-toastify';
import { IApp } from 'interface/appInterface';
import { Loader } from 'components/common/loader';
import { listStages } from 'services/stageService';
import { IStage } from 'interface/stageInterface';
import { Path } from 'router/constant';
import { useNavigate } from 'react-router-dom';
import { setStages } from 'redux-setup/slices/stageSlice';
import { listDeploymentVersions } from 'services/deploymentService';
import { IDeploymentVersion } from 'interface/deploymentInterface';
import './styles.scss';

export const Services = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [apps, setApps] = useState<IApp[]>([]);
  const [appStages, setAppStages] = useState<IStage[]>([]);
  const [deploymentVersions, setDeploymentVersions] = useState<
    IDeploymentVersion[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [page] = useState<number>(1);
  const limit = 25;
  const [groupedStages, setGroupedStages] = useState<{
    [key: string]: IStage[];
  }>({});

  const organizationID = useAppSelector(
    (state: RootState) => state.organization.id,
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await handleListApps();
      await handleListStages();
      await handleListDeploymentVersions();
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (appStages.length > 0) {
      groupStagesByApp();
    }
  }, [appStages]);

  const handleListApps = async () => {
    const response = await listApps({
      OrganizationID: organizationID,
      Page: page - 1,
      Limit: limit,
      Descending: true,
      OrderBy: 'Name',
    });
    const err = response.check();
    if (err) {
      toast.error('Error fetching services');
      setLoading(false);
      return;
    }
    const apps = response.unwrap();
    if (apps.Result && apps.Result.length > 0) {
      setApps(apps.Result);
      setLoading(false);
    } else {
      setApps([]);
      setLoading(false);
    }
  };

  const handleListStages = async () => {
    const response = await listStages({
      OrganizationID: organizationID,
      Page: page - 1,
      Limit: limit,
      Descending: true,
      OrderBy: 'Name',
    });
    const err = response.check();
    if (err) {
      toast.error('Error fetching services');
      setLoading(false);
      return;
    }
    const stages = response.unwrap();
    if (stages.Result && stages.Result.length > 0) {
      setAppStages(stages.Result);
      dispatch(setStages(stages.Result));
      setLoading(false);
    } else {
      setAppStages([]);
      setLoading(false);
    }
  };

  const handleListDeploymentVersions = async () => {
    const response = await listDeploymentVersions({
      OrganizationID: organizationID,
      Page: page - 1,
      Limit: limit,
      Descending: true,
      OrderBy: 'CreatedAt',
    });
    const err = response.check();
    if (err) {
      toast.error('Error fetching deployment versions');
      return;
    }
    const deploymentVersions = response.unwrap();
    if (deploymentVersions.Result && deploymentVersions.Result.length > 0) {
      setDeploymentVersions(deploymentVersions.Result);
      setLoading(false);
    } else {
      setDeploymentVersions([]);
      setLoading(false);
    }
  };

  const groupStagesByApp = () => {
    setLoading(true);
    const groupedStages = appStages.reduce<{ [key: string]: IStage[] }>(
      (acc, stage) => {
        acc[stage.RepositoryID] = acc[stage.RepositoryID] || [];
        acc[stage.RepositoryID].push(stage);
        return acc;
      },
      {},
    );
    setGroupedStages(groupedStages);
    setLoading(false);
  };

  const getDeploymentVersionForApp = (appName: string) => {
    return deploymentVersions.find(
      deployment => deployment.AppName === appName,
    );
  };

  return (
    <>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <Box p={3}>
          <div className="service-table-container">
            <div className="service-table">
              <div className="service-header">
                <div className="service-info-header">
                  <Typography variant="h6">Service</Typography>
                </div>
                <div className="environment-info-header">
                  <Typography variant="h6">Development</Typography>
                </div>
                <div className="environment-info-header">
                  <Typography variant="h6">Staging</Typography>
                </div>
                <div className="environment-info-header">
                  <Typography variant="h6">Production</Typography>
                </div>
                <div className="more-stages-header">
                  <Typography variant="h6">More Stages</Typography>
                </div>
              </div>

              {apps.length === 0 ? (
                <Box py={2}>
                  <Typography variant="body1" align="center">
                    No services found
                  </Typography>
                </Box>
              ) : (
                apps.map((app, index) => {
                  const appStages = groupedStages[app.RepositoryID] || [];
                  const deploymentVersion = getDeploymentVersionForApp(
                    app.Name,
                  );
                  const additionalStages =
                    appStages.length > 3 ? appStages.length - 3 : 0;

                  return (
                    <div
                      key={index}
                      className="service-row"
                      onClick={() => {
                        navigate(
                          `${Path.SERVICES}/${app.ID}/${app.RepositoryID}/${deploymentVersion?.ID}`,
                        );
                      }}>
                      <div className="service-info">
                        <Typography variant="subtitle1">{app.Name}</Typography>
                        <Typography variant="body2" className="service-type">
                          {app.Name}
                        </Typography>
                      </div>

                      {[0, 1, 2].map(index => (
                        <div key={index} className="environment-info">
                          {appStages[index] && (
                            <div className="version-info">
                              <Typography className="pb-2" variant="subtitle1">
                                {deploymentVersion?.AppVersion}
                                {appStages[index].Status?.toLowerCase() ===
                                'active' ? (
                                  <span className="ml-2 py-1 px-2 inline-flex items-center gap-x-1 text-xs font-medium bg-[#E7F6EC] text-[#027A48] rounded-full">
                                    <svg
                                      className="shrink-0 size-3"
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round">
                                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                                      <path d="m9 12 2 2 4-4"></path>
                                    </svg>
                                  </span>
                                ) : appStages[index].Status?.toLowerCase() ===
                                  'failed' ? (
                                  <span className="ml-2 py-1 px-2 inline-flex items-center gap-x-1 text-xs font-medium bg-[#FEE4E2] text-[#D92D20] rounded-full">
                                    <svg
                                      className="shrink-0 size-3"
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round">
                                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                                      <path d="M15 9l-6 6"></path>
                                      <path d="M9 9l6 6"></path>
                                    </svg>
                                  </span>
                                ) : appStages[index].Status?.toLowerCase() ===
                                  'deploying' ? (
                                  <span className="ml-2 py-1 px-2 inline-flex items-center gap-x-1 text-xs font-medium bg-[#EFF8FF] text-[#2E90FA] rounded-full">
                                    <svg
                                      className="shrink-0 size-3"
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round">
                                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"></path>
                                      <path d="M12 6v6"></path>
                                    </svg>
                                  </span>
                                ) : null}
                              </Typography>
                              <Typography className="pb-2" variant="body2">
                                {appStages[index].CreatedAt}
                              </Typography>
                              <Typography variant="body2">
                                <span
                                  className={`inline-flex items-center rounded-md px-4 py-1 text-sm min-w-[100%] font-medium justify-center ${
                                    appStages[index].Status?.toLowerCase() ===
                                    'active'
                                      ? 'bg-[#E7F6EC] text-[#027A48]'
                                      : appStages[
                                            index
                                          ].Status?.toLowerCase() === 'failed'
                                        ? 'bg-[#FEE4E2] text-[#D92D20]'
                                        : appStages[
                                              index
                                            ].Status?.toLowerCase() ===
                                            'deploying'
                                          ? 'bg-[#EFF8FF] text-[#2E90FA]'
                                          : 'bg-gray-100 text-gray-700'
                                  }`}>
                                  {appStages[index].Status}
                                </span>
                              </Typography>
                            </div>
                          )}
                        </div>
                      ))}

                      <div className="more-stages">
                        {additionalStages > 0 && (
                          <Typography variant="h6" className="stages-count">
                            +{additionalStages}
                          </Typography>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Box>
      )}
    </>
  );
};
